import { StytchB2BUIClient } from "@stytch/vanilla-js/b2b";

export type Id = string | number;

// The API's own explanation of a failure, dug out of the response body.
//
// PortalAPI answers errors with RFC 7807 ProblemDetails, where `detail` is the specific, human-readable
// message - "1080x1080 is square, which is not a shape any screen uses. Accepted: ..." - and `title` is the
// generic status name. Only `detail` is worth showing: `title` is "Bad Request", which tells a user no more
// than the status code already did, and would push aside the localized message a screen already has.
const detailFrom = (body: unknown): string | undefined => {
    if (typeof body === 'string') {
        const text = body.trim();
        if (!text || text.startsWith('<')) return undefined;

        // A JSON body that reached here as text. That happened for every error the API raises: PortalAPI
        // answers with RFC 7807, which is served as `application/problem+json`, and the callers tested the
        // content type with `includes('application/json')` — false for that, so the body was read as text and
        // returned verbatim. Users were shown the raw `{"type":...,"title":...,"detail":...}` instead of the
        // sentence inside it (TTD-143).
        //
        // The call sites now test for `json`, which covers every `+json` type. This stays as a second line of
        // defence: the failure mode is showing a user a wall of JSON, and it should not depend on every
        // present and future caller getting its content-type check right.
        if (text.startsWith('{') || text.startsWith('[')) {
            try {
                return detailFrom(JSON.parse(text));
            } catch {
                // Not JSON after all; fall through and treat it as the plain text it appears to be.
            }
        }

        return text.length <= 500 ? text : undefined;
    }

    if (!body || typeof body !== 'object') return undefined;
    const problem = body as { detail?: unknown; errors?: unknown; message?: unknown };

    if (typeof problem.detail === 'string' && problem.detail.trim()) return problem.detail.trim();

    // ASP.NET model validation answers with { errors: { Field: ["..."] } } and no detail.
    if (problem.errors && typeof problem.errors === 'object') {
        const messages = Object.values(problem.errors as Record<string, unknown>)
            .flatMap((v) => (Array.isArray(v) ? v : [v]))
            .filter((v): v is string => typeof v === 'string' && !!v.trim());
        if (messages.length) return messages.join(' ');
    }

    if (typeof problem.message === 'string' && problem.message.trim()) return problem.message.trim();

    return undefined;
};

export class HttpError extends Error {
    readonly status: number;
    readonly body: unknown;
    // The server's specific explanation, when it sent one. Undefined means it did not - NOT that the request
    // succeeded - so a caller with a localized fallback should prefer that over inventing wording here.
    readonly detail?: string;

    constructor(status: number, body: unknown) {
        const detail = detailFrom(body);
        // The message used to be `HTTP ${status}` unconditionally, so every screen that surfaced e.message
        // showed "HTTP 400" while the real explanation sat unread in `body`.
        super(detail ?? `HTTP ${status}`);
        this.status = status;
        this.body = body;
        this.detail = detail;
    }
}

// What to show a user when a request fails: the server's explanation if it sent one, otherwise the screen's
// own localized wording. Beats `e.message`, which falls back to "HTTP 500" - true, and of no use to anyone.
export const errorMessage = (e: unknown, fallback: string): string => {
    if (e instanceof HttpError) return e.detail ?? fallback;
    if (e instanceof Error && e.message) return e.message;
    return fallback;
};

export const getJwt = (): string | null | undefined => {
    const stytchClient = new StytchB2BUIClient(
        import.meta.env.VITE_STYTCH_PUBLIC_TOKEN as string,
    );
    return stytchClient.session.getTokens()?.session_jwt;    
};